import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, Mail, MessageSquare, Copy, Check, Loader2, Send, X, Clock, BellRing } from "lucide-react";
import { toast } from "sonner";
import { createInvite, listInvites, revokeInvite, nudgeInvite } from "../lib/api";
import { ROLES } from "./FamilyMemberForms";
import { useSecurity } from "../context/SecurityContext";
import { tr } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const copyText = (text) => {
  navigator.clipboard?.writeText(text);
  toast.success("Invite link copied");
};

// Build share links from the browser's own origin so they always match the address the family will use.
const withLinks = (inv) => {
  const join_url = `${window.location.origin}${inv.join_path}`;
  const text = `${inv.inviter} invited you to the family vault on HearthVault as ${inv.role_label}. Set up your PIN here: ${join_url}`;
  return { ...inv, join_url, sms_href: `sms:${inv.phone || ""}?&body=${encodeURIComponent(text)}` };
};

export const InviteResult = ({ invite: raw, onDone }) => {
  const [copied, setCopied] = useState(false);
  const invite = withLinks(raw);
  return (
    <div data-testid="invite-result" className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3">
      <div className="text-sm font-medium">
        Invite ready for {invite.name} <span className="text-muted-foreground font-normal">· {invite.role_label}</span>
      </div>
      {invite.email && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5" />
          {invite.email_sent ? `Email sent to ${invite.email}` : `Couldn't email ${invite.email} — share the link below instead`}
        </p>
      )}
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
        <Link2 className="h-4 w-4 text-primary shrink-0" />
        <span data-testid="invite-link" className="font-mono text-xs truncate flex-1">{invite.join_url}</span>
        <button
          data-testid="invite-copy-btn"
          onClick={() => {
            copyText(invite.join_url);
            setCopied(true);
          }}
          className="p-1 rounded-md hover:bg-accent"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
      <div className="flex gap-2">
        <Button asChild variant="outline" size="sm" className="flex-1 gap-1.5">
          <a data-testid="invite-sms-btn" href={invite.sms_href}>
            <MessageSquare className="h-3.5 w-3.5" /> Text it
          </a>
        </Button>
        <Button data-testid="invite-done-btn" size="sm" variant="ghost" onClick={onDone}>
          Done
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Link expires {new Date(invite.expires_at).toLocaleDateString()} · single use.</p>
    </div>
  );
};

export const InviteForm = ({ onCreated, onCancel }) => {
  const [name, setName] = useState("");
  const [role, setRole] = useState("partner");
  const security = useSecurity();
  const [standby, setStandby] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Who are you inviting?");
    setBusy(true);
    try {
      const inv = await createInvite({ name: name.trim(), role, email: email.trim(), phone: phone.trim(), standby: role.endsWith("_agent") && standby });
      onCreated(inv);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invite");
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  return (
    <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
      <Input data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Their name" className="bg-card" />
      <Select value={role} onValueChange={setRole}>
        <SelectTrigger data-testid="invite-role-select" className="bg-card">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ROLES.filter((r) => !r.admin || security?.role === "admin").map((r) => (
            <SelectItem key={r.value} value={r.value}>
              {r.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {ROLES.find((r) => r.value === role)?.hint && <p className="text-[11px] text-muted-foreground -mt-1" data-testid="invite-role-hint">{tr(ROLES.find((r) => r.value === role).hint)}</p>}
      {role.endsWith("_agent") && <label className="flex items-start gap-2 text-xs" data-testid="invite-standby"><input type="checkbox" className="mt-0.5" checked={standby} onChange={(e) => setStandby(e.target.checked)} data-testid="invite-standby-checkbox" /> <span><span className="font-medium">{tr("This is the backup")}</span> — {tr("they can verify their ID now; papers stay closed until you press Step in (Planning → Who does what).")}</span></label>}
      <Input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional — we'll send the link)" className="bg-card" />
      <Input data-testid="invite-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile (optional — for the Text it button)" className="bg-card" />
      <p className="text-[11px] text-muted-foreground -mt-1">They'll open the link and choose their own PIN. No password or app install needed.</p>
      <div className="flex gap-2">
        <Button data-testid="invite-send-btn" onClick={submit} disabled={busy} className="flex-1 gap-2 bg-primary hover:bg-primary/90">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Create invite
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export const PendingInvites = () => {
  const qc = useQueryClient();
  const { data: invites = [] } = useQuery({ queryKey: ["invites"], queryFn: listInvites });
  if (!invites.length) return null;

  const revoke = async (inv) => {
    try {
      await revokeInvite(inv.id);
      toast.success(`Invite for ${inv.name} revoked`);
      qc.invalidateQueries({ queryKey: ["invites"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    }
  };

  const nudge = async (inv) => {
    try {
      await nudgeInvite(inv.id);
      toast.success(`Reminder sent to ${inv.name}`);
      qc.invalidateQueries({ queryKey: ["invites"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminder");
    }
  };

  return (
    <div className="mt-3 space-y-1.5" data-testid="pending-invites">
      <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1">
        <Clock className="h-3 w-3" /> Pending invites <span className="normal-case tracking-normal">· auto-reminders after 3 and 7 days</span>
      </div>
      {invites.map(withLinks).map((inv) => (
        <div key={inv.id} data-testid="pending-invite-row" className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-1.5 text-xs">
          <span className="font-medium">{inv.name}</span>
          <span className="text-muted-foreground">· {inv.role_label}</span>
          {inv.expired && <span className="text-amber-600">· expired</span>}
          <span data-testid={`invite-pending-${inv.id}`} className="text-muted-foreground">· waiting {inv.days_pending}d{inv.nudges ? ` · nudged ${inv.nudges}×` : ""}</span>
          {inv.email && !inv.expired && (
            <button data-testid={`invite-nudge-${inv.id}`} onClick={() => nudge(inv)} className="ml-auto p-1 rounded hover:bg-accent text-primary" title="Send a reminder email">
              <BellRing className="h-3.5 w-3.5" />
            </button>
          )}
          <button data-testid={`invite-copy-${inv.id}`} onClick={() => copyText(inv.join_url)} className={`${inv.email && !inv.expired ? "" : "ml-auto "}p-1 rounded hover:bg-accent`} title="Copy link">
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button data-testid={`invite-revoke-${inv.id}`} onClick={() => revoke(inv)} className="p-1 rounded hover:bg-destructive/10 hover:text-destructive" title="Revoke">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};
